import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from "@angular/core";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from "@angular/material/dialog";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSnackBar } from "@angular/material/snack-bar";
import { CommonModule } from "@angular/common";
import { VenueService } from "app/shared/services/venue.service";
import {
  concatMap,
  defer,
  from,
  map,
  Observable,
  of,
  reduce,
  Subject,
  switchMap,
  tap,
  takeUntil,
} from "rxjs";
import { GoogleMapsModule, GoogleMap } from "@angular/google-maps";

@Component({
  selector: "app-edit-venue-dialog",
  templateUrl: "./edit-venue-dialog.component.html",
  styleUrl: "./edit-venue-dialog.component.scss",
  imports: [
    CommonModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatSelectModule,
    MatDialogModule,
    GoogleMapsModule,
    MatSlideToggleModule,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditVenueDialogComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  readonly dialogRef = inject(MatDialogRef<EditVenueDialogComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);

  @ViewChild(GoogleMap) map!: GoogleMap;
  @ViewChild("searchBox") searchBox!: ElementRef<HTMLInputElement>;

  venueForm: UntypedFormGroup;
  isLoading: boolean = false;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  // Google Maps properties
  center: google.maps.LatLngLiteral = { lat: 33.8938, lng: 35.5018 };
  zoom = 13;
  mapOptions: google.maps.MapOptions = {
    mapTypeId: "roadmap",
    zoomControl: true,
    streetViewControl: false,
    fullscreenControl: false,
  };

  location = {
    placeName: "",
    placeAddress: "",
    placeId: "",
    markerPos: null as google.maps.LatLngLiteral | null,
  };

  // Days of week for timing
  timing: Array<{
    name: string;
    checked: boolean;
    startTime: string;
    endTime: string;
  }> = [
    { name: "Sunday", checked: false, startTime: "", endTime: "" },
    { name: "Monday", checked: false, startTime: "", endTime: "" },
    { name: "Tuesday", checked: false, startTime: "", endTime: "" },
    { name: "Wednesday", checked: false, startTime: "", endTime: "" },
    { name: "Thursday", checked: false, startTime: "", endTime: "" },
    { name: "Friday", checked: false, startTime: "", endTime: "" },
    { name: "Saturday", checked: false, startTime: "", endTime: "" },
  ];

  // Time options (24-hour format)
  times: string[] = Array.from(
    { length: 25 },
    (_, i) => i.toString().padStart(2, "0") + ":00",
  );

  // Cancellation policy
  cancellationPolicy: any = {
    enabled: false,
    rules: [],
    defaultRefundPercentage: 0,
  };

  // Verification status
  verified: boolean = false;
  private initialVerified: boolean = false;
  // Onboarding status
  onBoarded: boolean = false;
  private initialOnBoarded: boolean = false;
  // Featured status
  featured: boolean = false;
  private initialFeatured: boolean = false;
  private initialDetailsSnapshot = "";
  private persistedVenue: any = {};

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private venueService: VenueService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {
    this.venueForm = this._formBuilder.group({
      name: ["", Validators.required],
      placeName: ["", Validators.required],
      placeAddress: [""],
      status: ["active", Validators.required],
      privacyPolicy: [""],
      rules: [""],
    });
  }

  ngOnInit(): void {
    if (this.data?.venue) {
      this.loadVenueData(this.data.venue);
    }
    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    // Initialize Google Maps autocomplete
    setTimeout(() => {
      if (this.searchBox?.nativeElement) {
        // Set initial value if location is already loaded
        if (this.location.placeName) {
          this.searchBox.nativeElement.value = this.location.placeName;
        }

        const ac = new google.maps.places.Autocomplete(
          this.searchBox.nativeElement,
          {
            fields: ["geometry", "name", "formatted_address", "place_id"],
          },
        );

        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          const loc = place.geometry?.location;
          if (!loc) return;

          this.center = { lat: loc.lat(), lng: loc.lng() };
          this.location.markerPos = this.center;
          this.zoom = 15;

          this.location.placeName = place.name ?? "";
          this.location.placeAddress = place.formatted_address ?? "";
          this.location.placeId = place.place_id ?? "";

          // Update form fields
          this.venueForm.patchValue({
            placeName: this.location.placeName,
            placeAddress: this.location.placeAddress,
          });

          this.cdr.markForCheck();
        });
      }
    }, 100);
  }

  onMapClick(ev: google.maps.MapMouseEvent): void {
    if (!ev.latLng) return;
    this.location.markerPos = { lat: ev.latLng.lat(), lng: ev.latLng.lng() };
    this.cdr.markForCheck();
  }

  private loadVenueData(venue: any): void {
    // Load basic info
    this.venueForm.patchValue({
      name: venue.name || "",
      placeName: venue.location?.placeName || "",
      placeAddress: venue.location?.placeAddress || "",
      status: venue.status || "active",
      privacyPolicy: venue.privacyPolicy || "",
      rules: venue.rules || "",
    });

    // Load location data for map
    if (venue.location) {
      this.location = {
        placeName: venue.location.placeName || "",
        placeAddress: venue.location.placeAddress || "",
        placeId: venue.location.placeId || "",
        markerPos: venue.location.markerPos || null,
      };
      if (venue.location.markerPos) {
        this.center = venue.location.markerPos;
        this.zoom = 15;
      }
    }

    // Load timing
    if (venue.timing) {
      let timingData = venue.timing;

      // Handle if timing is a JSON string
      if (typeof timingData === "string") {
        try {
          timingData = JSON.parse(timingData);
        } catch (e) {
          console.error("Error parsing timing:", e);
          timingData = [];
        }
      }

      if (Array.isArray(timingData)) {
        // Map the timing data to our timing array
        const timingMap = new Map(timingData.map((t: any) => [t.name, t]));

        this.timing = this.timing.map((day) => {
          const existingDay = timingMap.get(day.name);
          if (existingDay) {
            return {
              ...day,
              checked: existingDay.checked || false,
              startTime: existingDay.startTime || "",
              endTime: existingDay.endTime || "",
            };
          }
          return day;
        });
      }
    }

    // Load cancellation policy
    if (venue.cancellationPolicy) {
      this.cancellationPolicy = {
        enabled: venue.cancellationPolicy.enabled || false,
        rules: venue.cancellationPolicy.rules || [],
        defaultRefundPercentage:
          venue.cancellationPolicy.defaultRefundPercentage || 0,
      };
    }

    // Load verification status
    this.verified = venue.verified === true;
    this.initialVerified = this.verified;

    // Load onboarding status
    this.onBoarded = venue.onBoarded === true;
    this.initialOnBoarded = this.onBoarded;
    // Load featured status
    this.featured = venue.featured === true;
    this.initialFeatured = this.featured;
    this.persistedVenue = { ...venue };
    this.initialDetailsSnapshot = this.serializeVenueDetails(
      this.buildVenueData(),
    );
    // Force change detection
    this.cdr.detectChanges();

    // OR mark for check twice
    this.cdr.markForCheck();

    console.log("Loaded venue data:", {
      name: venue.name,
      verified: this.verified,
      onBoarded: this.onBoarded,
      featured: this.featured,
    });

    this.cdr.markForCheck();
  }

  validateTimings(): string[] {
    const errors: string[] = [];
    const checkedDays = this.timing.filter((day) => day.checked);

    if (checkedDays.length === 0) {
      errors.push("At least one day must be selected");
    }

    checkedDays.forEach((day) => {
      if (!day.startTime || !day.endTime) {
        errors.push(`${day.name} must have both start and end times`);
      } else if (day.startTime >= day.endTime) {
        errors.push(`${day.name} end time must be after start time`);
      }
    });

    return errors;
  }

  addCancellationRule(): void {
    if (!this.cancellationPolicy.rules) {
      this.cancellationPolicy.rules = [];
    }
    this.cancellationPolicy.rules.push({
      hoursBeforeBooking: 24,
      refundPercentage: 50,
    });
    this.cdr.markForCheck();
  }

  removeCancellationRule(index: number): void {
    if (this.cancellationPolicy.rules) {
      this.cancellationPolicy.rules.splice(index, 1);
      this.cdr.markForCheck();
    }
  }

  onSubmit(): void {
    const errors = this.validateTimings();
    if (!this.location.markerPos) {
      errors.push("Select the venue location on the map");
    }
    if (this.venueForm.invalid || this.isLoading || errors.length > 0) {
      if (errors.length > 0) {
        this.snackBar.open(errors[0], "Close", { duration: 3000 });
      }
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    const venueData = this.buildVenueData();
    const detailsSnapshot = this.serializeVenueDetails(venueData);
    const detailsChanged = detailsSnapshot !== this.initialDetailsSnapshot;
    const desiredStatuses = {
      verified: this.verified,
      onBoarded: this.onBoarded,
      featured: this.featured,
    };

    console.log("Submitting venue data:", venueData);

    const venueId = this.data.venue._id;
    const detailsUpdate$ = detailsChanged
      ? defer(() =>
          this.venueService.createOrUpdateVenue({
            id: venueId,
            ...venueData,
          }),
        ).pipe(
          tap((response) => {
            this.initialDetailsSnapshot = detailsSnapshot;
            this.persistedVenue = this.mergeVenue(
              { ...this.persistedVenue, ...venueData },
              response,
            );
          }),
          map(() => this.persistedVenue),
        )
      : of(this.persistedVenue);

    const statusUpdates: Array<{
      field: "verified" | "onBoarded" | "featured";
      value: boolean;
      request: () => Observable<any>;
      commit: () => void;
    }> = [];

    if (desiredStatuses.verified !== this.initialVerified) {
      statusUpdates.push({
        field: "verified",
        value: desiredStatuses.verified,
        request: () =>
          this.venueService.verifyVenue(venueId, desiredStatuses.verified),
        commit: () => {
          this.initialVerified = desiredStatuses.verified;
        },
      });
    }
    if (desiredStatuses.onBoarded !== this.initialOnBoarded) {
      statusUpdates.push({
        field: "onBoarded",
        value: desiredStatuses.onBoarded,
        request: () =>
          this.venueService.setVenueOnboarding(
            venueId,
            desiredStatuses.onBoarded,
          ),
        commit: () => {
          this.initialOnBoarded = desiredStatuses.onBoarded;
        },
      });
    }
    if (desiredStatuses.featured !== this.initialFeatured) {
      statusUpdates.push({
        field: "featured",
        value: desiredStatuses.featured,
        request: () =>
          this.venueService.setVenueFeatured(venueId, desiredStatuses.featured),
        commit: () => {
          this.initialFeatured = desiredStatuses.featured;
        },
      });
    }

    detailsUpdate$
      .pipe(
        switchMap((detailsVenue) =>
          statusUpdates.length === 0
            ? of(detailsVenue)
            : from(statusUpdates).pipe(
                concatMap((update) =>
                  defer(update.request).pipe(
                    map((response) => {
                      update.commit();
                      this.persistedVenue = {
                        ...this.mergeVenue(this.persistedVenue, response),
                        [update.field]: update.value,
                      };
                      return this.persistedVenue;
                    }),
                  ),
                ),
                reduce((_venue, updatedVenue) => updatedVenue, detailsVenue),
              ),
        ),
        map((finalVenue) => ({ success: true, data: { ...finalVenue } })),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe({
        next: (response) => {
          this.snackBar.open("Venue updated successfully", "Close", {
            duration: 3000,
          });
          this.dialogRef.close(response);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error("Error updating venue:", err);
          this.snackBar.open(
            "Error updating venue: " +
              (err.error?.message || err.message || "Unknown error"),
            "Close",
            { duration: 5000 },
          );
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private buildVenueData(): any {
    const formValue = this.venueForm.value;
    return {
      name: formValue.name,
      status: formValue.status,
      location: {
        placeName: formValue.placeName || this.location.placeName,
        placeAddress: formValue.placeAddress || this.location.placeAddress,
        placeId: this.location.placeId,
        markerPos:
          this.location.markerPos || this.data?.venue?.location?.markerPos,
      },
      timing: this.timing,
      rules: formValue.rules || "",
      cancellationPolicy: {
        enabled: this.cancellationPolicy.enabled,
        rules: this.cancellationPolicy.rules || [],
        defaultRefundPercentage:
          this.cancellationPolicy.defaultRefundPercentage || 0,
      },
      privacyPolicy: formValue.privacyPolicy || "",
    };
  }

  private serializeVenueDetails(venueData: any): string {
    return JSON.stringify(venueData);
  }

  private mergeVenue(baseVenue: any, response: any): any {
    const responseVenue = response?.data ?? response;
    return responseVenue && typeof responseVenue === "object"
      ? { ...baseVenue, ...responseVenue }
      : { ...baseVenue };
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
